// preflop-facts.ts - the BOARDLESS preflop engine→coach contract (PF3).
//
// The preflop analogue of facts.ts's EngineFacts / FactsDigest, for a hand that has no board yet. It is
// the load-bearing firewall seam PF3 owns: where the postflop digest carries a `board.textureClass`, the
// preflop digest has NO board field AT ALL - the type literally cannot hold a card string or a texture, so
// the "no hole cards / no card strings" firewall (ARCHITECTURE.md §5) holds by construction one street
// earlier. The coach fields are the preflop-specific ones the coach reasons over: the chart region hero's
// combo resolved in, hero's positional class, the effective-stack bucket, and the pot type.
//
//   PreflopEngineFacts  - the FULL record (exact hole cards + bb amounts) stored per decision, mirroring
//                         EngineFacts. Internal; never crosses to the coach.
//   PreflopFactsDigest  - the bucketed PROJECTION that is the ONLY thing the coach receives. No board, no
//                         texture, no hole cards, no dollars - boardless + card-free by construction.
import { z } from 'zod';
import { CardIndexSchema } from './primitives';
import { PositionSchema, PositionClassSchema } from './position';
import { ActionKindSchema } from './action';
import { ImbalanceProfileSchema, ImbalanceSummarySchema } from './archetype';
import { PotTypeSchema } from './exploit-rule';
import { GameTypeSchema, ModeIdSchema } from './scenario';
import { SprBucketSchema, StackDepthBucketSchema, GradeBandSchema, IcmPressureSchema } from './buckets';
import { RangeShapeSchema, FiredRuleSchema } from './facts';
import { PreflopIntentSchema, PreflopRoleSchema, PreflopRecommendationSchema, } from './preflop';
// ── shared leaf shapes ─────────────────────────────────────────────────────────
/** The graded user preflop decision (the preflop analogue of UserActionSchema). Carries the poker-intent
 *  label + its mechanical action + the grade band; NO typed dollars in the digest projection. */
export const PreflopUserActionSchema = z.object({
    intent: PreflopIntentSchema,
    action: ActionKindSchema,
    gradeBand: GradeBandSchema,
    /** evLoss in bb; null when the model abstains (PF2 role gate), absent when ungraded. */
    evLossBB: z.number().nullable().optional(),
    errorCodes: z.array(z.string()).optional(),
});
/** The preflop math + provenance block, bb-denominated (the full record keeps exact amounts). */
export const PreflopMathSchema = z.object({
    role: PreflopRoleSchema,
    potType: PotTypeSchema,
    /** hero's effective stack in big blinds (exact). */
    effectiveStackBB: z.number().nonnegative(),
    /** the coarse effective-stack bucket the coach reads (10/20/40/80/150bb). */
    stackDepthBucket: StackDepthBucketSchema,
    /** the depth proxy the shipped preflop rule layer keys on (sprBucket-as-depth, preflop.ts convention). */
    depthProxyBucket: SprBucketSchema,
    /** amount hero must call, in bb (the open's to-amount when defending); absent when first-in. */
    toCallBB: z.number().nonnegative().optional(),
    /** current pot in bb. */
    potBB: z.number().nonnegative(),
    /** the first raiser's position, when there was a raise (defend / vs-3bet). */
    openerPosition: PositionSchema.optional(),
    /** the shipped chart key hero's combo resolved against (provenance), or 'none' for a fold. */
    chartRegion: z.string(),
    /** the effective range-set family id after any widen/tighten shift (e.g. 'live_loose_6max'). */
    family: z.string(),
    /** true iff hero's combo was in the resolved chart region. */
    inChart: z.boolean(),
});
// ── PreflopEngineFacts (full record) ────────────────────────────────────────────
export const PreflopEngineFactsSchema = z.object({
    v: z.literal(1),
    handId: z.string(),
    street: z.literal('preflop'),
    decisionIndex: z.number().int().nonnegative(),
    context: z.object({
        gameType: GameTypeSchema,
        mode: ModeIdSchema,
        position: PositionSchema,
        positionClass: PositionClassSchema,
        playersInHand: z.number().int().positive(),
        icmPressure: IcmPressureSchema.optional(),
    }),
    /** exact hole cards - full record ONLY; never projected to the digest (mirrors EngineFacts.hero). */
    hero: z.object({
        holeCards: z.tuple([CardIndexSchema, CardIndexSchema]),
    }),
    preflop: PreflopMathSchema,
    villain: z.object({
        seat: z.number().int().nonnegative(),
        archetypeId: z.string(),
        imbalances: ImbalanceProfileSchema,
        rangeShape: RangeShapeSchema,
    }),
    recommendation: PreflopRecommendationSchema,
    userAction: PreflopUserActionSchema.optional(),
});
// ── PreflopFactsDigest (coach-facing projection) ────────────────────────────────
// Bucketed/enumerated conclusions ONLY. BOARDLESS BY CONSTRUCTION: there is no board / texture / card /
// dollar field on this type, so the firewall cannot be violated by a projection bug - the type has nowhere
// to put a card. Its canonical serialization is the preflop coach cache key.
export const PreflopFactsDigestSchema = z.object({
    v: z.literal(1),
    street: z.literal('preflop'),
    context: z.object({
        gameType: GameTypeSchema,
        mode: ModeIdSchema,
        positionClass: PositionClassSchema,
        position: PositionSchema,
        playersInHand: z.number().int().positive(),
        icmPressure: IcmPressureSchema.optional(),
    }),
    /** the preflop node hero is at. */
    role: PreflopRoleSchema,
    /** pot type (limped / srp / 3bp / 4bp) - the preflop analogue of the postflop texture class. */
    potType: PotTypeSchema,
    /** the effective-stack bucket the coach reasons over (the preflop "depth" the whole spot hinges on). */
    stackDepthBucket: StackDepthBucketSchema,
    /** the chart region hero's combo resolved in (provenance the coach can name), or 'none'. */
    chartRegion: z.string(),
    /** true iff hero's combo was in the resolved chart region. */
    inChart: z.boolean(),
    /** the opener's position, when hero is defending / facing a re-raise. */
    openerPosition: PositionSchema.optional(),
    villain: z.object({
        archetypeId: z.string(),
        imbalances: ImbalanceSummarySchema,
        rangeShape: RangeShapeSchema,
    }),
    recommendation: z.object({
        intent: PreflopIntentSchema,
        action: ActionKindSchema,
        /** the sizing CONVENTION label (e.g. '3x 3bet', 'iso 3bb+1/limper') - never a dollar amount. */
        sizingLabel: z.string().optional(),
        firedRules: z.array(z.object({ id: z.string(), principle: z.string(), confidence: FiredRuleSchema.shape.confidence })),
        alternatives: z.array(z.object({ intent: PreflopIntentSchema })),
    }),
    userAction: z
        .object({
        intent: PreflopIntentSchema,
        action: ActionKindSchema,
        gradeBand: GradeBandSchema,
    })
        .optional(),
});
//# sourceMappingURL=preflop-facts.js.map